import React, { useState } from 'react';
import { ConfirmModal } from '../../../commons/components/ConfirmModal';
import useApi from '../../../api/backendApiContext';
import { useQueryCacheManager } from '../../../hooks/useQueryCacheManager';
import { useMutation } from '@tanstack/react-query';
import { PauseSubscriptionOption, Customer, INVOICE_TYPE_ISSUED, IssuedInvoice } from '../../types';
import useAnalyticsPage from '../../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../../web-analytics/Page';
import moment from 'moment';
import { useFetchedBillingInvoices } from '../../hooks/useFetchedBillingInvoices';
import { PauseSubscriptionInvoicesModal } from '../pause/PauseSubscriptionInvoicesModal';
import { isInvoicePayable } from '../../utils';
import { useNotification } from '../../../notification/notificationsContext';
import { analyticsTrack } from '../../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../../web-analytics/AnalyticsEvent';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import clsx from 'clsx';

const formatMonthAndDay = (date: Date) => moment(date).format('MMMM D');

export const PauseStep = (props: {
  pauseOptions: PauseSubscriptionOption[];
  onNext: () => void;
  onGoBackClicked: () => void;
  onClosed: () => void;
}) => {
  useAnalyticsPage(Page.CANCELLATION_WIZARD_PAUSE);

  const { pauseOptions, onNext, onGoBackClicked, onClosed } = props;
  const { billingApi } = useApi();
  const [selectedPauseOption, setSelectedPauseOption] = useState(
    pauseOptions.length > 0 ? pauseOptions[0] : null
  );
  const queryCacheManager = useQueryCacheManager();

  const { notifySuccess, notifyError } = useNotification();
  const { mutate: pausePlan, status: pausePlanStatus } = useMutation(
    async () => billingApi.pauseBillingPlan(selectedPauseOption!!.monthsToPause),
    {
      onError: async () => {
        await queryCacheManager.invalidateBillingCustomer();
        notifyError({
          notificationMsg:
            'Failed to pause your subscription. Please try again later or contact support.',
        });
      },
      onSuccess: async (customer: Customer) => {
        await queryCacheManager.updateBillingCustomer(customer);
        notifySuccess({ notificationMsg: 'Your subscription has been paused.' });
        analyticsTrack(AnalyticsEvent.BILLING_PLAN_PAUSED);
        onClosed();
      },
    }
  );
  const maxMonths = _.max(pauseOptions.map((x) => x.monthsToPause)) ?? 1;

  const [payInvoicesModalKey, setPayInvoicesModalKey] = useState<number | null>(null);
  const {
    query: { data: invoices },
  } = useFetchedBillingInvoices({ refetchOnWindowFocus: 'always' });

  const [unpaidInvoiceIds, setUnpaidInvoiceIds] = useState<string[] | null>(null);
  return (
    <ConfirmModal
      title="Pause your account"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      footer={{
        type: 'show',
        cancelButton: {
          label: 'Dismiss',
          onClick: () => {},
        },
        extraButtons: [
          {
            label: 'Go back',
            onClick: onGoBackClicked,
          },
          {
            label: 'Continue cancelation',
            variant: 'secondary',
            disabled: pausePlanStatus === 'loading' || pausePlanStatus === 'success',
            onClick: onNext,
          },
          {
            label: selectedPauseOption
              ? `Pause for ${selectedPauseOption.monthsToPause} month${
                  selectedPauseOption.monthsToPause > 1 ? 's' : ''
                }`
              : 'Pause',
            variant: 'primary',
            disabled:
              pausePlanStatus === 'loading' ||
              pausePlanStatus === 'success' ||
              !selectedPauseOption,
            onClick: () => {
              if (!invoices) return;
              const unpaidInvoices = invoices
                .filter((x) => x.type === INVOICE_TYPE_ISSUED)
                .filter(isInvoicePayable) as IssuedInvoice[];
              if (unpaidInvoices && unpaidInvoices.length > 0) {
                setUnpaidInvoiceIds(unpaidInvoices.map((inv) => inv.id));
                setPayInvoicesModalKey(new Date().valueOf());
              } else {
                pausePlan();
              }
            },
          },
        ],
      }}
    >
      <>
        <div className="cancellation-intro__description">
          <span>
            {`We’ll pause your subscription so you won’t receive any new invoices for up to ${maxMonths} months.
            You can change your mind anytime, it is easy to resume your subscription or cancel
            through our dashboard. Please note that all outstanding invoices need to be paid before
            we can pause the account.`}
          </span>
          <div className="pause-step__months-container">
            {pauseOptions.map((x) => (
              <Button
                type="button"
                key={`pause-option-${x.monthsToPause}`}
                className={clsx('multilevel-tab-panel__top-level-tab', {
                  'multilevel-tab-panel__top-level-tab--selected':
                    x.monthsToPause === selectedPauseOption?.monthsToPause,
                })}
                onClick={() => setSelectedPauseOption(x)}
              >
                {`Pause for ${x.monthsToPause} month${x.monthsToPause > 1 ? 's' : ''}`}
              </Button>
            ))}
          </div>
          {selectedPauseOption && (
            <div className="pause-step__timeline">
              <div className="pause-step__timeline-item">
                <div className="pause-step__timeline-icon pause-step__timeline-icon--active">
                  <div className="pause-step__timeline-icon--active-internal" />
                </div>
                <div className="pause-step__timeline-content">
                  <p className="growegy-font-em">
                    {`${formatMonthAndDay(selectedPauseOption.pauseAt)} — Your ${
                      selectedPauseOption.monthsToPause
                    }-month pause begins`}
                  </p>
                  <p>Your pause begins one day before your next billing date.</p>
                </div>
              </div>

              <div className="pause-step__timeline-item">
                <div className="pause-step__timeline-icon">🔔</div>
                <div className="pause-step__timeline-content">
                  <p className="growegy-font-em">
                    {formatMonthAndDay(selectedPauseOption.remindAt)}
                  </p>
                  <p>We’ll remind you before your pause ends.</p>
                </div>
              </div>

              <div className="pause-step__timeline-item">
                <div className="pause-step__timeline-icon">👑</div>
                <div className="pause-step__timeline-content">
                  <p className="growegy-font-em">
                    {formatMonthAndDay(selectedPauseOption.resumeAt)}
                  </p>
                  <p>
                    Your subscription resumes and your next invoice will automatically be sent,
                    unless you’ve already canceled or resumed your subscription early.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {payInvoicesModalKey && invoices && unpaidInvoiceIds && unpaidInvoiceIds.length > 0 && (
          <PauseSubscriptionInvoicesModal
            key={payInvoicesModalKey}
            invoices={
              invoices.filter(
                (inv) =>
                  inv.type === INVOICE_TYPE_ISSUED && unpaidInvoiceIds.find((id) => id === inv.id)
              ) as IssuedInvoice[]
            }
          />
        )}
      </>
    </ConfirmModal>
  );
};
